import { FolderForTree } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder/FolderForTree';
import { IFolderTreeNode } from './FolderTree';
import { RecentFolderId, SystemFolderID } from './FolderTreeUtils';
import { fetchWithParameters, postWithParameters } from 'utils/ApiUtils';
import { ITreeNode } from 'components/Tree/CommonTree/CommonTreeNode';
import { FolderForTreeWrapper } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Portal';

export interface IFolderNodeLoader {
  getRootFolders(): Promise<FolderForTree[]>;
  getSubFolders(folderId: string): Promise<FolderForTree[]>;
  getSubFolder(folderId: string): Promise<FolderForTree>;
  createFolderNode(folder: FolderForTree, parent?: ITreeNode<IFolderTreeNode>): ITreeNode<IFolderTreeNode>;
}

export async function getMyRootFoldersForTree() {
  return (await (
    await fetchWithParameters({ url: '/FolderData/GetMyRootFoldersForTree', urlParams: {} })
  ).json()) as FolderForTree[];
}

export async function getMyHomeFolder() {
  return (await (
    await fetchWithParameters({ url: '/FolderData/GetMyHomeFolder', urlParams: {} })
  ).json()) as FolderForTree;
}

//We call the GetSubFolderForTree and GetSubFoldersForTree methods from the FolderDataController to get and cache icons
export async function getSubFolderForTree(folderId: string) {
  return (await (
    await postWithParameters({ url: '/FolderData/GetSubFolderForTree', urlParams: { folderId: folderId } })
  ).json()) as FolderForTree;
}

export async function getMyRecentFoldersForTree() {
  return (await (
    await fetchWithParameters({ url: '/FolderData/GetMyRecentFoldersForTree', urlParams: {} })
  ).json()) as FolderForTree[];
}

export async function getSubFoldersForTree(folderId: string) {
  return (
    await postWithParameters({ url: '/FolderData/GetSubFoldersForTree', urlParams: { folderId: folderId } })
  ).json();
}

export class DefaultNodeLoader implements IFolderNodeLoader {
  async getRootFolders(): Promise<FolderForTree[]> {
    const rootPromise = getMyRootFoldersForTree();
    const homePromise = getMyHomeFolder();
    return await Promise.all([rootPromise, homePromise]).then(([root, home]) => {
      if (home) {
        root.push(home);
      }

      return root.filter(n => n.FolderID !== SystemFolderID);
    });
  }

  async getSubFolders(folderId: string): Promise<FolderForTree[]> {
    let subFoldersForTree: FolderForTree[];

    if (folderId === RecentFolderId) {
      subFoldersForTree = await getMyRecentFoldersForTree();
    } else {
      subFoldersForTree = await getSubFoldersForTree(folderId);
    }

    return subFoldersForTree;
  }
  async getSubFolder(folderId: string): Promise<FolderForTree> {
    return getSubFolderForTree(folderId);
  }

  createFolderNode(folder: FolderForTree, parent?: ITreeNode<IFolderTreeNode>): ITreeNode<IFolderTreeNode> {
    return createTreeNode(folder, '', false, parent);
  }
}

export class RecentNodeLoader extends DefaultNodeLoader {
  async getSubFolders(folderId: string): Promise<FolderForTree[]> {
    return (await super.getSubFolders(folderId)).map(p => {
      return { ...p, CanBeExpanded: false };
    });
  }

  async getRootFolders(): Promise<FolderForTree[]> {
    return this.getSubFolders(RecentFolderId);
  }
}

export class SystemNodeLoader extends DefaultNodeLoader {
  async getRootFolders(): Promise<FolderForTree[]> {
    const rootPromise = this.getSubFolders(SystemFolderID);
    const systemPromise = this.getSubFolder(SystemFolderID);
    return await Promise.all([rootPromise, systemPromise]).then(([systemRoots, systemfolder]) => {
      if (systemfolder) {
        systemRoots.unshift({ ...systemfolder, HasSubFolders: false, CanBeExpanded: false, OrderNum: -99 });
      }

      return systemRoots;
    });
  }

  async getSubFolder(folderId: string): Promise<FolderForTree> {
    let subFolder = await getSubFolderForTree(folderId);

    if (subFolder.FolderID === SystemFolderID) {
      subFolder = { ...subFolder, HasSubFolders: false, CanBeExpanded: false, OrderNum: -99 };
    }

    return subFolder;
  }
}

export function createTreeNode(
  folderNode: FolderForTree,
  selectedFolderId: string,
  isInitialLoading?: boolean,
  parent?: ITreeNode<IFolderTreeNode> | null
): ITreeNode<IFolderTreeNode> {
  const folderId = folderNode.FolderID || '';

  let node = {
    nodeId: folderNode.FolderID,
    isSelectable: true,
    name: folderNode.FolderName,
    data: { folderNode: folderNode, parent },
    isActive: folderId === selectedFolderId,
    isExpanded: isInitialLoading ? folderId === selectedFolderId : false,
    hasChildren: folderNode.CanBeExpanded,
    icon: folderNode.IconUrl ? <img className="dp-folder-tree__icon" src={folderNode.IconUrl} alt="" /> : undefined,
    style: { color: folderNode.Color ? folderNode.Color : 'inherit' },
  } as ITreeNode<IFolderTreeNode>;

  return node;
}

export function createTreeNodes(
  folderNodes: FolderForTree[],
  selectedFolderId: string,
  isInitialLoading?: boolean,
  parent?: ITreeNode<IFolderTreeNode> | null
): ITreeNode<IFolderTreeNode>[] {
  return folderNodes.map(folder => createTreeNode(folder, selectedFolderId, isInitialLoading, parent));
}

export function getTreeNodesForFolderForTreeWrapper(
  trees: FolderForTreeWrapper[],
  selectedFolderId: string,
  parent?: ITreeNode<IFolderTreeNode> | null
): ITreeNode<IFolderTreeNode>[] {
  const nodes = trees.map(wrapper => {
    const children = wrapper.SubFolders as FolderForTreeWrapper[] | null;

    if (parent) {
      // because immer chokes on circular references, we need to pass a copy of the parent for the child to hold,
      // that does not also reference the child(ren). Child needs parent _data_ but not ref to original parent node.
      parent = { ...parent };
      delete parent.childNodes;
    }

    const node: ITreeNode<IFolderTreeNode> = createTreeNode(wrapper.FolderForTree, selectedFolderId, false, parent);
    if (children?.length) {
      node.isExpanded = true;
      node.childNodes = getTreeNodesForFolderForTreeWrapper(children, selectedFolderId, node);
    }
    return node;
  });
  return nodes;
}

export async function loadFolderTreeNodes(
  loader: IFolderNodeLoader,
  parent: ITreeNode<IFolderTreeNode>,
  selectedFolderId: string
): Promise<ITreeNode<IFolderTreeNode>[]> {
  const parentId = parent.data!.folderNode.FolderID || '';

  const subFolders: FolderForTree[] = await loader.getSubFolders(parentId);
  return subFolders.map(folderNode => createTreeNode(folderNode, selectedFolderId, false, parent));
}
